import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { PackagingFieldCrossCheckStoredEntry } from '@/shared/hooks/api/packagingSuppliers'
import { PackagingAssessmentRunDetailSheet } from './PackagingAssessmentRunDetailSheet'
import {
  formatRunTimestamp,
  PackagingFieldCrossCheckRunResults,
} from './PackagingFieldCrossCheckRunResults'
import { isManualOverrideEntry } from './packagingAuditOverrideValidation'

type PackagingFieldCrossCheckHistoryTableProps = {
  history: PackagingFieldCrossCheckStoredEntry[]
  locale: string
}

function FieldCrossCheckHistorySummary({
  entry,
}: {
  entry: PackagingFieldCrossCheckStoredEntry
}) {
  const { t } = useTranslation('packagingSuppliers')

  if (isManualOverrideEntry(entry)) {
    return (
      <div className='flex flex-wrap items-center gap-2'>
        {entry.flagged ? (
          <Badge variant='destructive'>{t('fieldCrossCheck.flagged')}</Badge>
        ) : (
          <Badge variant='secondary'>{t('fieldCrossCheck.clear')}</Badge>
        )}
        <span className='text-sm text-muted-foreground'>
          {entry.manualOverride.reason}
        </span>
      </div>
    )
  }

  const failedCount = entry.checks.filter((check) => !check.passed).length

  return (
    <div className='flex flex-wrap items-center gap-2'>
      {entry.flagged ? (
        <Badge variant='destructive'>{t('fieldCrossCheck.flagged')}</Badge>
      ) : (
        <Badge variant='secondary'>{t('fieldCrossCheck.clear')}</Badge>
      )}
      {entry.flagged ? (
        <span className='text-sm text-muted-foreground'>
          {t('itemDetail.audits.history.failedChecksCount', {
            count: failedCount,
          })}
        </span>
      ) : (
        <span className='text-sm text-muted-foreground'>
          {t('itemDetail.audits.history.allChecksPassed')}
        </span>
      )}
    </div>
  )
}

export function PackagingFieldCrossCheckHistoryTable({
  history,
  locale,
}: PackagingFieldCrossCheckHistoryTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(
    null
  )

  const selectedEntry = history.find(
    (entry) => entry.timestamp === selectedTimestamp
  )
  const selectedTitle =
    selectedEntry != null
      ? formatRunTimestamp(selectedEntry.timestamp, locale)
      : ''

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('itemDetail.audits.history.columnRun')}</TableHead>
            <TableHead>
              {t('itemDetail.audits.history.columnSummary')}
            </TableHead>
            <TableHead className='w-[1%] text-end'>
              <span className='sr-only'>
                {t('itemDetail.audits.history.columnActions')}
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((entry) => {
            const runLabel = formatRunTimestamp(entry.timestamp, locale)
            return (
              <TableRow key={entry.timestamp}>
                <TableCell className='font-medium'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <span>{runLabel}</span>
                    {isManualOverrideEntry(entry) ? (
                      <Badge variant='outline'>
                        {t('itemDetail.manualOverride.tag')}
                      </Badge>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell>
                  <FieldCrossCheckHistorySummary entry={entry} />
                </TableCell>
                <TableCell className='text-end'>
                  <Button
                    type='button'
                    variant='link'
                    className='h-auto px-0'
                    onClick={() => {
                      setSelectedTimestamp(entry.timestamp)
                    }}
                  >
                    {t('itemDetail.audits.history.viewResults')}
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <PackagingAssessmentRunDetailSheet
        open={selectedEntry != null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTimestamp(null)
          }
        }}
        title={selectedTitle}
      >
        {selectedEntry != null && isManualOverrideEntry(selectedEntry) && (
          <p className='text-sm text-muted-foreground whitespace-pre-wrap'>
            {selectedEntry.manualOverride.reason}
          </p>
        )}
        {selectedEntry != null && !isManualOverrideEntry(selectedEntry) && (
          <PackagingFieldCrossCheckRunResults result={selectedEntry} />
        )}
      </PackagingAssessmentRunDetailSheet>
    </>
  )
}
